import React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';

import { dividerLabel } from './constructor/lineModel';
import { Tooltip } from '@/components/ui/tooltip';

function rowHtml(row: string): string {
  return row
    .replace(/^<blockquote>/, '')
    .replace(/<\/blockquote>$/, '')
    .replace(/<a\s+/gi, '<a target="_blank" rel="noreferrer" ');
}

/**
 * Plain read-only render of a `body_html` — used by ConstructorView and
 * InteractiveView for report formats they don't structurally model (legacy
 * pre-2026-09-03 sentence formats; see `isTreeFormat` in constructor/
 * lineModel.ts). Alexey (2026-09-03, direct instruction): "Старые форматы
 * уже uneditable должны стать, только просмотр" — no pencil/circle edit
 * affordances, no inline charts, just the formatted text.
 *
 * The ━ divider section include/exclude toggle (D3) still works here — it
 * doesn't depend on any per-line classification, just the divider marker
 * itself, so there's no reason to take it away along with structured
 * editing.
 */
export function ReadOnlyBody({
  value,
  hiddenSections,
  onToggleSection,
  disabled,
}: {
  value: string;
  hiddenSections: Set<string>;
  onToggleSection: (key: string) => void;
  disabled?: boolean;
}) {
  const { t } = useT();
  const rows = value.split('\n');
  let curKey = '';
  let curHidden = false;

  return (
    <div className="report-body flex flex-col rounded-md border border-border bg-background p-3 text-sm leading-relaxed">
      {rows.map((row, i) => {
        if (row.includes('━')) {
          curKey = dividerLabel(row);
          curHidden = hiddenSections.has(curKey);
          const key = curKey;
          return (
            <div key={i} className="mb-1 mt-3 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span
                className={cn(
                  'text-[11px] font-semibold uppercase tracking-wider',
                  curHidden ? 'text-muted-foreground/50 line-through' : 'text-muted-foreground',
                )}
              >
                {key}
              </span>
              <Tooltip content={t(curHidden ? 'reportValidation.constructor.unhide' : 'reportValidation.constructor.hide')}><button
                type="button"
                onClick={() => onToggleSection(key)}
                disabled={disabled}
                aria-label={t(curHidden ? 'reportValidation.constructor.unhide' : 'reportValidation.constructor.hide')}
                className={cn(
                  'shrink-0 rounded p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground',
                  disabled && 'pointer-events-none opacity-30',
                )}
              >
                {curHidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              </button></Tooltip>
              <span className="h-px flex-1 bg-border" />
            </div>
          );
        }

        if (curHidden) return null;
        if (row.trim() === '') return <div key={i} className="h-2" />;
        if (row.startsWith('<blockquote')) {
          return <blockquote key={i} className="mt-0.5" dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />;
        }
        return <div key={i} className="py-0.5" dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />;
      })}
    </div>
  );
}
